import { toFile } from '@Ravnur-Inc/core/transformers/base64';
import isSame from '@Ravnur-Inc/nanoutils/isSame';
import some from '@Ravnur-Inc/nanoutils/some';
import { Player$Source } from '@Ravnur-Inc/player/src/types/Source';
import { Vue, Options } from 'vue-class-component';

import MediaRepository from '@/repositories/media-repository';
import SourcesRepository from '@/repositories/sources-repository';
import { ThumbnailInfo } from '@/types/Entity';
import { Media$Details, Media$Type } from '@/types/Media';

import './media-thumbnails.scss';

const CN = 'media-thumbnails';
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const repository = new MediaRepository();
const sourcesRepository = new SourcesRepository();
const THUMBNAIL_MAX_SIZE = 150 * 1024; // 150KB

class Props {
  origin: Media$Details;
}

@Options({
  emits: ['stored'],
  watch: {
    mediaId: {
      immediate: true,
      handler(this: MediaThumbnails) {
        this.loadSources();
      },
    },
  },
  inject: {
    reload: 'reload',
  },
})
export default class MediaThumbnails extends Vue.with(Props) {
  declare $refs: {
    canvas: HTMLCanvasElement | undefined;
  };
  declare reload: () => void;

  private processing = false;
  private sources: Nullable<Player$Source | Player$Source[]> = null;

  get local() {
    return {};
  }

  get thumbnails(): ThumbnailInfo[] {
    return this.origin.optionalThumbnails || [];
  }

  get mediaId() {
    return this.origin.id;
  }

  get isVideo() {
    return some(isSame(this.origin.type), [Media$Type.VIDEO, Media$Type.VIDEO_PLAYLIST]);
  }

  protected async loadSources() {
    if (!this.isVideo) {
      return;
    }
    try {
      // fix RAV-4191
      this.sources = await sourcesRepository.pull(this.mediaId, !isSafari);
    } catch (e) {
      this.$processException(e);
    }
  }

  render() {
    return (
      <div class={CN}>
        <div class={[`${CN}__row`]}>
          <file-button class={`${CN}__custom-file-btn`} onFileSelected={this.uploadNewThumbnail}>
            <icon type="upload" />
            <l10n group="media" tkey="action__add-thumbnail" />
          </file-button>
          {this.isVideo && (
            <action-button class={`${CN}__screenshot-btn`} process={this.takeScreenshot}>
              <icon type="camera" />
              <l10n group="media" tkey="action__take-screenshot" />
            </action-button>
          )}
          <canvas ref="canvas" class={`${CN}__canvas`} />
          {this.thumbnails.map(this.renderThumbnail)}
        </div>
        {this.renderSpinner()}
      </div>
    );
  }

  private renderSpinner() {
    if (!this.processing) {
      return;
    }
    return (
      <overlay class={`${CN}__overlay`} enable={true}>
        <spinner />
      </overlay>
    );
  }

  private renderThumbnail(info: ThumbnailInfo) {
    const { origin } = this;
    const entity = { ...origin, thumbnail: info };
    const isCurrent = origin.thumbnail && origin.thumbnail.name === info.name;
    const cn = {
      [`${CN}__thumbnail`]: true,
      [`${CN}__thumbnail--current`]: isCurrent,
    };
    return (
      <div class={isCurrent ? `${CN}__overlay` : ''}>
        <icon class={`${CN}__overlay-check`} type="check-circle" v-show={isCurrent} />
        <thumbnail
          key={info.name}
          class={cn}
          entity={entity}
          onclick={() => this.changeThumbnail(info)}
        />
      </div>
    );
  }

  private async changeThumbnail(thumbnail: ThumbnailInfo) {
    this.processing = true;

    try {
      const media = { ...this.origin, thumbnail };
      await repository.save(media);
      this.reload();
    } catch (e) {
      this.$processException(e);
    }

    this.processing = false;
  }

  private async uploadNewThumbnail(file: File): Promise<void> {
    const { origin: media } = this;
    if (!media) {
      return;
    }
    try {
      await repository.uploadThumbnail(file, media);
      this.reload();
    } catch (e) {
      this.$processException(e);
    }
  }

  private async takeScreenshot() {
    const { canvas } = this.$refs;

    if (!canvas) {
      return;
    }

    const video = document.querySelector('video');

    if (!(video instanceof HTMLVideoElement)) {
      return;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    try {
      let base64 = canvas.toDataURL('image/jpeg');
      let file = await toFile(base64, `${Date.now()}.jpeg`, 'image/jpeg');

      let quality = (THUMBNAIL_MAX_SIZE * 95) / file.size / 100;

      while (file.size > THUMBNAIL_MAX_SIZE) {
        quality -= 0.01; // Reduce quality incrementally
        base64 = canvas.toDataURL('image/jpeg', quality);
        file = await toFile(base64, `${Date.now()}.jpeg`, 'image/jpeg');
      }

      await this.uploadNewThumbnail(file);
    } catch (e) {
      this.$processException(e);
    }
  }
}
